import { Link } from 'react-router';

import routes from '@/assets/constants/routes-no.ts';
import { EconomySection } from '@/components/marketing/economy-section/index.ts';
import { Section } from '@/components/marketing/section/index.ts';
import { SplitHeroSection } from '@/components/marketing/split-hero-section/index.ts';
import { TestimonialsSection } from '@/components/marketing/testimonials-section/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { testimonialsQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';

import type { Route } from './+types/debt-register';
import smartphoneSrc from './internal/smartphone.svg';

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const testimonials = await queryClient.query(testimonialsQuery());
  return { testimonials };
}

export default function DebtRegister({ loaderData }: Route.ComponentProps) {
  const { toggleChat } = useBrainSession();

  const debtCheckCtaAction = () => {
    sendUserPortalEvent('main_cta_clicked');
    toggleChat({
      goal: 'debt_check',
      step: 'skip_start_set_vipps_auth_test',
      clientKnowledge: {},
    });
  };
  return (
    <Page title="Gjeldsregisteret">
      <div className="flex w-full flex-col bg-white">
        <article className="flex flex-col gap-8 sm:gap-15 md:mt-15">
          <SplitHeroSection.Root>
            <SplitHeroSection.Content>
              <SplitHeroSection.Title>
                Sjekk din gjeld
                <br />i Gjeldsregisteret
              </SplitHeroSection.Title>

              <SplitHeroSection.List>
                <SplitHeroSection.ListItem>
                  <span>
                    <b>Gratis</b> oppslag i Gjeldsregisteret med BankID.
                  </span>
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>
                  Se kredittscore og sjekk om du har betalingsanmerkninger.
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>Se om du kan få lavere rente.</SplitHeroSection.ListItem>
              </SplitHeroSection.List>

              <Button color="yellow" className="mb-4 md:mb-6 lg:mb-8" action={debtCheckCtaAction}>
                Fortsett med BankID
              </Button>

              <p className="max-w-md text-center typography-v1-body3">
                Ved å fortsette aksepterer jeg Scoopr sine{' '}
                <Link
                  to={routes.TERMS_OF_USE_MIN_SCOOPR}
                  className="text-[#18A0FB] hover:underline"
                >
                  brukervilkår
                </Link>{' '}
                og{' '}
                <Link to={routes.PRIVACY_STATEMENT} className="text-[#18A0FB] hover:underline">
                  personvernerklæring
                </Link>
              </p>
            </SplitHeroSection.Content>

            <SplitHeroSection.ImageContainer>
              <SplitHeroSection.Image
                src={smartphoneSrc}
                width={220}
                height={250}
                alt="gjeldsregisteret"
              />
            </SplitHeroSection.ImageContainer>
          </SplitHeroSection.Root>

          <Section.Container className="bg-white text-v1-background-200">
            <Section.Content className="py-8">
              <div className="flex max-w-2xl flex-col gap-8">
                <h2 className="typography-v1-h2 text-pretty">Hva er gjeldsregisteret?</h2>

                <p className="typography-v1-body2">
                  Gjeldsregisteret er et offentlig system som gir banker og långivere oversikt over
                  usikret gjeld hos privatpersoner i Norge. Formålet er å redusere risikoen for
                  overbelåning og gi både forbrukere og finansinstitusjoner bedre kontroll over
                  økonomien.
                </p>

                <p className="typography-v1-body2">
                  Ordningen ble innført i Norge i 2019 og er regulert av gjeldsinformasjonsloven.
                  Hensikten er å gjøre det vanskeligere å ta opp mer gjeld enn man har økonomi til å
                  håndtere.
                </p>

                <h2 className="typography-v1-h2 text-pretty">Hvordan sjekke sin gjeld?</h2>

                <p className="typography-v1-body2">
                  Å ha oversikt over egen gjeld er viktig for å få kontroll på økonomien og ta gode
                  økonomiske valg. Mange nordmenn har kredittkort, forbrukslån eller andre former
                  for usikret kreditt uten full oversikt over hvor mye de faktisk skylder. Heldigvis
                  finnes det enkle måter å sjekke dette på. Hos Scoopr gjør vi det enklere å forstå
                  egen økonomi og få oversikt over gjelden din.
                </p>
              </div>
            </Section.Content>
          </Section.Container>

          <TestimonialsSection data={loaderData.testimonials} />
          <EconomySection />
        </article>
      </div>
    </Page>
  );
}
